import { toast } from "react-toastify";
import { handleFilterChange } from "../../../../helpers";
import DeleteModal from "../../../../components/common/DeleteModal";

import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import { useState } from "react";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  orderOrdering,
  orderStatusChange,
} from "../../../../services/orderService";
import dayjs from "dayjs";
import ActionBar from "../../../../components/elements/ActionBar";
import { lngDetect } from "../../../../helpers/lngDetect";
import Indentification from "../../../../components/elements/Indentification";
import {
  organizationReviewDelete,
  organizationReviewList,
} from "../../../../services/reviewService";
import RatingStars from "../../../elements/RatingStars";
import StatusUpdateModal from "../../../common/StatusUpdateModal";
import Button from "../../../form/Button";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { useTranslation } from "react-i18next";

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved":
      return "bg-green-100 text-green-700";
    case "rejected":
      return "bg-red-100 text-red-700";
    case "pending":
    default:
      return "bg-yellow-100 text-yellow-700";
  }
};

// Status Badge Component
const StatusBadge = ({ status, onClick }) => {
  const { t } = useTranslation();

  const colorClass = getStatusColor(status);

  return (
    <Button
      buttonFunction={onClick}
      buttonClasses={`px-4 py-1 rounded-full text-xs font-medium capitalize transition-all duration-200 hover:shadow-md ${colorClass} hover:opacity-80`}
      buttonLabel={t(status || "pending")}
    />
  );
};

const OrganizationReview = ({ data, organizationDetailsRefetch }) => {
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState([]);
  const [modalOpen, setModalOpen] = useState({ isOpen: false, data: null });
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [userkeyword, setuserKeyword] = useState("");
  const usersearch = useDebounce(userkeyword, 300);
  const [expandedReviews, setExpandedReviews] = useState(new Set());
  const [statusModal, setStatusModal] = useState({ isOpen: false, data: null });
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  const {
    data: reviewData,
    isLoading: reviewLoading,
    isFetching: reviewFetching,
  } = useQuery({
    queryKey: ["review", filters, data?._id],
    queryFn: async () => {
      const res = await organizationReviewList({
        ...filters,
        referenceId: data?._id,
      });

      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!data?._id,
  });

  // Toggle review expansion
  const toggleReviewExpansion = (reviewId) => {
    setExpandedReviews((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(reviewId)) {
        newSet.delete(reviewId);
      } else {
        newSet.add(reviewId);
      }
      return newSet;
    });
  };

  // Truncate text utility
  const truncateText = (text, maxLength = 150) => {
    if (!text) return "-";
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  // Handle status update success
  const handleStatusUpdateSuccess = (res) => {
    // Update the cache immediately for better UX
    queryClient.setQueryData(["review", filters, data?._id], (state) => {
      if (state && res?.data) {
        const updatedData = (state?.data || []).map((item) =>
          item._id === res.data._id ? { ...item, ...res.data } : item
        );

        return {
          ...state,
          data: updatedData,
        };
      }
      return state;
    });

    // Also invalidate to ensure fresh data
    queryClient.invalidateQueries(["review", filters, data?._id]);
  };

  const tableHeadData = [
    {
      _id: 1,
      name: t("Reviewer"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Organization"),
      align: "left",
    },
    {
      _id: 3,
      name: t("Rating"),
      align: "left",
      ...tableFilter({
        filterKey: "rating",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "5 Stars", value: "5", type: "checkbox" },
          { name: "4 Stars", value: "4", type: "checkbox" },
          { name: "3 Stars", value: "3", type: "checkbox" },
          { name: "2 Stars", value: "2", type: "checkbox" },
          { name: "1 Star", value: "1", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 4,
      name: t("Review Message"),
      align: "left",
    },
    {
      _id: 5,
      name: t("Status"),
      align: "left",
      ...tableFilter({
        filterKey: "status",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val }));
        },
        filterList: [
          { name: "Pending", value: "pending", type: "checkbox" },
          { name: "Approved", value: "approved", type: "checkbox" },
          { name: "Rejected", value: "rejected", type: "checkbox" },
        ],
      }),
    },
    {
      _id: 6,
      name: t("Date"),
      align: "left",
    },
    {
      _id: 7,
      name: t("Action"),
      align: "left",
    },
  ];

  // Table Body Data =====================>
  const tableData = reviewData?.data?.map((item) => {
    const isExpanded = expandedReviews.has(item?._id);
    const reviewMessage = item?.message ?? "";
    const shouldTruncate = reviewMessage.length > 150;

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[180px]",
          data: (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <Indentification
                  className={
                    "shadow-md bg-transparent border-2 border-gray-100"
                  }
                  type="circle"
                  size="md"
                  image={item?.userData?.image && item?.userData?.image?.url}
                  defaultClass={"!bg-primary/10 !text-primary !font-semibold"}
                />
              </div>
              <div className="flex flex-col">
                <div className="font-semibold text-gray-900">
                  {item?.userData?.firstName || item?.userData?.lastName
                    ? `${item?.userData?.firstName ?? ""} ${
                        item?.userData?.lastName ?? ""
                      }`.trim()
                    : "-"}
                </div>
              </div>
            </div>
          ),
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[160px]",
          data: (
            <div className="flex items-center">
              <div className="font-medium text-gray-900">
                {lngDetect(item?.organizationData?.name) ?? "N/A"}
              </div>
            </div>
          ),
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: item?.rating ? <RatingStars data={item.rating} /> : "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[200px] !max-w-[400px]",
          data: (
            <div className="space-y-2">
              <div className="text-gray-700 leading-relaxed">
                {shouldTruncate && !isExpanded
                  ? truncateText(reviewMessage)
                  : reviewMessage || "No review message"}
              </div>
              {shouldTruncate && (
                <button
                  onClick={() => toggleReviewExpansion(item?._id)}
                  className="text-sm text-primary hover:text-primary/80 font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      <span>Show Less</span>
                      <i className="fas fa-chevron-up text-xs"></i>
                    </>
                  ) : (
                    <>
                      <span>Read More</span>
                      <i className="fas fa-chevron-down text-xs"></i>
                    </>
                  )}
                </button>
              )}
            </div>
          ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <StatusBadge
              status={item?.status}
              onClick={() =>
                setStatusModal({
                  isOpen: true,
                  data: item,
                })
              }
            />
          ),
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[120px]",
          data: (
            <div className="flex flex-col">
              <div className="text-gray-900 font-medium">
                {dayjs(item?.createdAt).format("MMM DD, YYYY") || "-"}
              </div>
              <div className="text-xs text-gray-500">
                {dayjs(item?.createdAt).format("hh:mm A") || ""}
              </div>
            </div>
          ),
        },
        {
          _id: "action",
          type: "action",
          align: "left",
          cellClass: "w-[5%]",
          actionData: [
            {
              _id: "delete",
              name: t("Delete"),
              icon: "fa-regular fa-trash-can",
              standout: true,
              onClick: () => {
                setDeleteModal({
                  isOpen: true,
                  data: item,
                });
              },
            },
          ],
        },
      ],
    };
  });

  // add or edit page =================>
  function pageManageResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        if (filters?.page === 1) {
          queryClient.setQueryData(["review", filters, data?._id], (state) => {
            if (state) {
              let stateData = JSON.parse(JSON.stringify(state?.data));
              stateData = [...stateData, res?.data];
              return { ...state, data: stateData };
            }
          });
          if (reviewData?.metadata?.totalDocs > filters?.limit) {
            queryClient.invalidateQueries(["review", filters, data?._id]);
          }
        } else {
          setFilters((pre) => ({ ...pre, page: 1 }));
        }
      }
      if (res?.status === 200) {
        queryClient.setQueryData(["review", filters, data?._id], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = stateData?.map((item) =>
              item?._id === res?.data?._id ? { ...item, ...res?.data } : item
            );
            return { ...state, data: stateData };
          }
        });
        if (reviewData?.metadata?.totalDocs > filters?.limit) {
          queryClient.invalidateQueries(["review", filters, data?._id]);
        }
      }
      setModalOpen({ isOpen: false, data: null });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  //   Review delete handler =================>
  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await organizationReviewDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });

        // Update the cache immediately for better UX
        queryClient.setQueryData(["review", filters, data?._id], (state) => {
          if (state) {
            const updatedData = (state?.data || []).filter(
              (item) => !payload?.includes(item?._id)
            );

            return {
              ...state,
              data: updatedData,
              metadata: {
                ...state?.metadata,
                totalDocs: (state?.metadata?.totalDocs || 0) - payload.length,
              },
            };
          }
        });

        // Also invalidate to refetch fresh data
        queryClient.invalidateQueries(["review", filters, data?._id]);

        // Show success message
        toast.success(
          `Review${payload.length > 1 ? "s" : ""} deleted successfully`
        );
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
      toast.error("Failed to delete review. Please try again.");
    },
  });

  const options = [
    {
      id: "userDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "User Delete",
    },
  ];

  function actionHandler(val) {
    switch (val) {
      case "userDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }

  const { mutate: handleOrdering, isPending } = useMutation({
    mutationFn: async ({ newData, oldData }) => {
      const res = await orderOrdering({
        ordering_data: newData?.map((item, i) => ({
          id: item?._id,
          ordering: i,
        })),
      });
      return { res, newData, oldData };
    },
    onSuccess: ({ res, newData, oldData }) => {
      if (res?.status !== 200) {
        queryClient.setQueryData(["review", filters, data?._id], (state) => {
          if (state) {
            return { ...state, data: oldData };
          }
        });
        toast.warning(res?.message || res?.error);
      }
    },
  });

  // Helper function to get active filter count
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.rating && filters.rating.length > 0) count++;
    if (filters.status && filters.status.length > 0) count++;
    return count;
  };

  // Helper function to clear all filters
  const clearAllFilters = () => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters.rating;
      delete newFilters.status;
      return { ...newFilters, page: 1 };
    });
  };

  return (
    <div className="space-y-7 relative mx-auto bg-white shadow py-4 px-4 rounded-md">
    <div className="w-full flex flex-col pb-10 bg-white rounded-md border border-gray-200">
      <div className="space-y-8 py-8 px-5">
        <div className="flex justify-between items-center border-b border-gray-300">
          <div className="flex flex-col gap-2 w-full">
            <div className="font-bold text-lg xl:text-2xl">
              {t("Review management")}
            </div>
            <div className="text-sm xl:text-base h-10 text-gray-600">
              {t(
                "Manage customer reviews: view detailed feedback, ratings, and moderate review content."
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end">
          <div className="flex  flex-wrap items-center gap-3">
            <div className="w-full md:w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={reviewData?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            {/* <div className="w-full md:w-72">
              <Search
                placeholder={t("Search reviews, reviewers, organizations")}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div> */}
            {getActiveFilterCount() > 0 && (
              <div className="flex items-center gap-2">
                <Button
                  buttonLabel={t(`Clear Filters (${getActiveFilterCount()})`)}
                  variant="outline"
                  buttonClasses="text-xs px-3 py-2 h-10"
                  buttonFunction={clearAllFilters}
                />
              </div>
            )}
            {selectedItems?.length > 0 && (
              <div className="flex items-center gap-2">
                <ActionBar options={options} onChange={actionHandler} />
                <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                  {selectedItems?.length} {t("Selected")}
                  <button
                    onClick={() => setSelectedItems([])}
                    type="button"
                    className="cursor-pointer"
                  >
                    <i className="fa-fw fa-light fa-xmark"></i>
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>

        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={reviewLoading}
          pageCount={reviewData?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
            setExpandedReviews(new Set()); // Clear expanded reviews on page change
          }}
          originalData={[...(reviewData?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["review", filters, data?._id],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          handleOrdering={(newData, oldData) =>
            handleOrdering({ newData, oldData })
          }
          isFetching={reviewFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal({ isOpen: false, data: null })}
        reviewData={statusModal.data}
        onSuccess={handleStatusUpdateSuccess}
      />

      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div></div>
  );
};

export default OrganizationReview;
